import { FileText, Download, BookOpen, ArrowRight, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { money } from "./shared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VISUALS = [
  ["quote_photo_url", "Exterior", "portal-restricted-render-exterior"],
  ["interior_photo_url", "Interior", "portal-restricted-render-interior"],
  ["kitchen_photo_url", "Kitchen", "portal-restricted-render-kitchen"],
  ["bathroom_photo_url", "Bathroom / WC", "portal-restricted-render-bathroom"],
];

export function PortalRestricted({ booking, payDeposit }) {
  const visuals = VISUALS.filter(([k]) => booking?.[k]);
  const pdfHref = `${api.defaults.baseURL}/portal/quote-pdf`;
  const siteVisit = booking?.status === "site_visit_booked";
  const visitWhen = booking?.site_visit_date
    ? `${new Date(booking.site_visit_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}${booking.site_visit_time ? ` at ${booking.site_visit_time}` : ""}`
    : "";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="portal-restricted">
      <div className="lg:col-span-2 space-y-6">
        {siteVisit ? (
          <div className="de-card p-6 border-[#1F6B3B]" data-testid="portal-site-visit-card">
            <div className="label-eyebrow mb-2 text-[#1F6B3B] flex items-center gap-2"><CalendarCheck size={14} /> Free site visit booked</div>
            <div className="text-xl font-black text-[#12263A]" data-testid="portal-site-visit-when">{visitWhen || "Date to be confirmed"}</div>
            <p className="text-sm text-[#6B7280] mt-2">About an hour at {booking.address || booking.postcode || "your property"}. We&rsquo;ll check access, measure up and talk through your design and quote. <b>Nothing has been paid and nothing is due now</b> &mdash; your deposit is only paid <b>after the visit</b>, and only if you decide to go ahead. When you&rsquo;re ready, you can reserve your start date below.</p>
          </div>
        ) : (
          <div className="de-card p-6 border-[#C79A3B]">
            <div className="label-eyebrow mb-2 text-[#C79A3B]">Booking fee not yet paid</div>
            <p className="text-sm text-[#6B7280]">Your design and quote are saved. Reserve your start date &amp; on-site survey with a <b>£{Math.round(booking.booking_fee||booking.deposit_total||499).toLocaleString("en-GB")} booking fee</b>. It comes straight off your 10% deposit &mdash; the balance is only due once your survey is signed off and you&rsquo;re happy to proceed.</p>
          </div>
        )}

        {visuals.length > 0 && (
          <div className="de-card p-6" data-testid="portal-restricted-visuals">
            <div className="label-eyebrow mb-4">Your design visuals</div>
            <div className={`grid gap-4 ${visuals.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}>
              {visuals.map(([k, label, tid]) => (
                <div key={k} className="border border-[#E5E7EB] bg-white overflow-hidden">
                  <img src={`${BACKEND}${booking[k]}`} alt={`Extension design — ${label.toLowerCase()}`} loading="lazy"
                    onError={(e) => { e.target.parentElement.style.display = "none"; }}
                    className="w-full h-56 object-cover bg-[#F4F4F4]" data-testid={tid} />
                  <div className="text-xs uppercase tracking-widest text-[#6B7280] px-3 py-2">{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="de-card p-6 sticky top-6">
          <div className="label-eyebrow mb-4">Your quote</div>
          <div className="text-sm space-y-2">
            {siteVisit
              ? <div className="flex justify-between"><span>Site visit</span><b>{visitWhen || "TBC"}</b></div>
              : <div className="flex justify-between"><span>Start date</span><b>{booking.start_date}</b></div>}
            <div className="flex justify-between"><span>Contract total (incl. VAT)</span><b>{money(booking.total_inc_vat)}</b></div>
            <div className="flex justify-between border-t border-[#E5E7EB] pt-2 mt-2"><span>{siteVisit ? "Booking fee (after your visit)" : "Booking fee to pay now"}</span><b className="text-[#C79A3B]">{money(booking.booking_fee || booking.deposit_total)}</b></div>
            {Number(booking.deposit_balance_due||0) > 0 && (
              <div className="flex justify-between text-xs text-[#6B7280]"><span>Balance of 10% deposit</span><span>{money(booking.deposit_balance_due)} <span className="text-[10px]">after survey</span></span></div>
            )}
          </div>
          <button data-testid="portal-pay-deposit" onClick={payDeposit} className={`${siteVisit ? "de-btn-secondary" : "de-btn-primary"} w-full justify-center mt-5`}>
            {siteVisit ? "Ready to go ahead? Pay £" : "Pay £"}{Math.round(booking.booking_fee||booking.deposit_total||499).toLocaleString("en-GB")} &amp; reserve slot
          </button>
          <a data-testid="portal-download-quote" href={pdfHref} target="_blank" rel="noreferrer"
            className="de-btn-secondary w-full justify-center mt-2 inline-flex items-center gap-2">
            <Download size={15} /> Download quote PDF
          </a>
          <p className="text-xs text-[#9CA3AF] mt-3 text-center">Secure payment via Stripe. Reserves your start date &amp; on-site survey.</p>
        </div>

        <div className="de-card p-4 flex items-start gap-3">
          <FileText size={18} className="text-[#C79A3B] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B7280]">Your quote PDF is available now — you don&apos;t need to have paid the deposit to view or save it.</p>
        </div>

        <Link to="/blog" data-testid="portal-restricted-insights-link"
          className="de-card p-4 flex items-center gap-3 hover:border-[#C79A3B] transition-colors group">
          <BookOpen size={18} className="text-[#C79A3B] shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[#12263A]">Insights &amp; Guides</div>
            <div className="text-xs text-[#6B7280]">Planning, build tips &amp; project advice.</div>
          </div>
          <ArrowRight size={14} className="text-[#C79A3B] shrink-0 group-hover:translate-x-0.5 transition-transform"/>
        </Link>
      </div>
    </div>
  );
}
